import { useCallback, useEffect, useState } from 'react';
import { CustomButton } from '../../../../components/CustomButton';
import {
  ContainerTab,
  Field,
  IconButtonPromo,
  Row,
  TabHeaderRow,
  EmptyState,
  ListItemCard,
  Badge,
  InfoBanner,
} from '../styles';
import { daysLabels } from '../../../../constants/daysLabels';
import { FaEdit, FaClock, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import EditarCadastrarHorarioPromo from './EditarCadastrar';
import { Label } from '../../../../components/Label';
import { CustomInput } from '../../../../components/CustomInput';
import api from '../../../../services/api';
import { promotionsDateTypes } from '../../../../enums/promotionsTypes';
import { formatTime, normalizeDateTime } from '../utils';

const ordenarHorarios = (lista) =>
  [...lista].sort(
    (a, b) => a.Dia - b.Dia || String(a.Inicio).localeCompare(String(b.Inicio)),
  );

const splitDateTime = (value) => {
  const [date = '', time = ''] = normalizeDateTime(value).split(' ');
  return [date, formatTime(time)];
};

const HorariosPromocao = ({
  promo,
  tipoData,
  inicio,
  fim,
  setInicio,
  setFim,
  ensurePromocao,
  onSchedulesCountChange,
  pendenteAtivacao,
}) => {
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [schedule, setSchedule] = useState();
  const [modalCreate, setModalCreate] = useState(false);
  const [dateInitial, setDateInitial] = useState(
    () => splitDateTime(inicio)[0],
  );
  const [timeInitial, setTimeInitial] = useState(
    () => splitDateTime(inicio)[1],
  );
  const [dateFinal, setDateFinal] = useState(() => splitDateTime(fim)[0]);
  const [timeFinal, setTimeFinal] = useState(() => splitDateTime(fim)[1]);
  const [promoAtual, setPromoAtual] = useState(promo);
  const [preparando, setPreparando] = useState(false);

  const isDataFixa = tipoData === promotionsDateTypes.DATA_FIXA;
  const qtdAtivos = schedules.filter((s) => s.Ativo).length;

  useEffect(() => {
    setPromoAtual(promo);
  }, [promo]);

  const abrirModalHorario = async (item) => {
    setPreparando(true);
    const alvo = await ensurePromocao?.();
    setPreparando(false);
    if (!alvo) return;

    setPromoAtual(alvo);
    setSchedule(item || null);
    setModalCreate(true);
  };

  const loadSchedules = useCallback(async () => {
    if (!promo?.Id) return;
    setLoadingSchedules(true);
    await api
      .get(`horarios-promocao/listar/${promo.Id}`)
      .then((response) => setSchedules(response.data))
      .catch((error) => console.log('Erro ao listar horarios' + error))
      .finally(() => setLoadingSchedules(false));
  }, [promo?.Id]);

  useEffect(() => {
    if (isDataFixa) loadSchedules();
  }, [isDataFixa, loadSchedules]);

  useEffect(() => {
    onSchedulesCountChange?.(loadingSchedules ? null : qtdAtivos);
  }, [loadingSchedules, onSchedulesCountChange, qtdAtivos]);

  useEffect(() => {
    if (isDataFixa) return;
    setInicio(
      dateInitial && timeInitial ? `${dateInitial} ${timeInitial}` : null,
    );
    setFim(dateFinal && timeFinal ? `${dateFinal} ${timeFinal}` : null);
  }, [
    dateInitial,
    timeInitial,
    dateFinal,
    timeFinal,
    isDataFixa,
    setInicio,
    setFim,
  ]);

  return (
    <>
      <ContainerTab>
        {isDataFixa ? (
          <>
            <TabHeaderRow>
              <h3>Dias da Semana</h3>
              <CustomButton
                appearance="primary"
                onClick={() => abrirModalHorario(null)}
                disabled={preparando}
                loading={preparando}
              >
                Adicionar Horário
              </CustomButton>
            </TabHeaderRow>

            <InfoBanner>
              <FaInfoCircle size={14} />
              <span>
                Cada horário é salvo ao confirmar no formulário. Quando
                terminar, clique em <b>Continuar</b> para configurar os produtos
                participantes.
              </span>
            </InfoBanner>

            {!loadingSchedules && (pendenteAtivacao || qtdAtivos === 0) && (
              <InfoBanner variant="warning">
                <FaExclamationTriangle size={14} />
                <span>
                  <b>Obrigatório para Data fixa:</b> cadastre ao menos um horário
                  ativo. {pendenteAtivacao
                    ? 'A promoção está inativa e será ativada ao clicar em Continuar.'
                    : 'Sem horários, a promoção não pode ser ativada.'}
                </span>
              </InfoBanner>
            )}

            {schedules.length === 0 ? (
              <EmptyState>
                <FaClock size={24} color="#cbd5e1" />
                <p>
                  {loadingSchedules
                    ? 'Carregando horários...'
                    : 'Nenhum horário definido.'}
                </p>
                {!loadingSchedules && (
                  <span style={{ fontSize: 12 }}>
                    Selecione os dias e horários da semana em que esta promoção
                    será válida.
                  </span>
                )}
              </EmptyState>
            ) : (
              schedules.map((item) => (
                <ListItemCard key={item.Id}>
                  <div className="info-group">
                    <span className="title">{daysLabels[item?.Dia]}</span>
                    <span className="subtitle">
                      <FaClock size={10} color="#94a3b8" />{' '}
                      {formatTime(item.Inicio)} às {formatTime(item.Fim)}
                    </span>
                  </div>
                  <div className="actions">
                    <Badge active={item.Ativo}>
                      {item.Ativo ? 'Ativo' : 'Inativo'}
                    </Badge>
                    <IconButtonPromo onClick={() => abrirModalHorario(item)}>
                      <FaEdit size={16} color="#64748b" />
                    </IconButtonPromo>
                  </div>
                </ListItemCard>
              ))
            )}
          </>
        ) : (
          <>
            <TabHeaderRow>
              <h3>Período de Validade</h3>
            </TabHeaderRow>
            <InfoBanner>
              <FaInfoCircle size={14} />
              <span>
                Informe quando a promoção começa e termina. O período é salvo ao
                clicar em <b>Salvar e continuar</b>.
              </span>
            </InfoBanner>
            <div
              style={{
                background: '#f8fafc',
                padding: 20,
                borderRadius: 10,
                border: '1px solid #e2e8f0',
              }}
            >
              <Row style={{ marginBottom: 15 }}>
                <Field>
                  <Label>Data de Início</Label>
                  <CustomInput
                    type="date"
                    value={dateInitial}
                    onChange={setDateInitial}
                  />
                </Field>
                <Field>
                  <Label>Hora de Início</Label>
                  <CustomInput
                    type="time"
                    value={timeInitial}
                    onChange={setTimeInitial}
                  />
                </Field>
              </Row>
              <Row>
                <Field>
                  <Label>Data Final (Término)</Label>
                  <CustomInput
                    type="date"
                    value={dateFinal}
                    onChange={setDateFinal}
                  />
                </Field>
                <Field>
                  <Label>Hora Final</Label>
                  <CustomInput
                    type="time"
                    value={timeFinal}
                    onChange={setTimeFinal}
                  />
                </Field>
              </Row>
            </div>
          </>
        )}
      </ContainerTab>

      {modalCreate && (
        <EditarCadastrarHorarioPromo
          open={modalCreate}
          handleClose={() => setModalCreate(false)}
          schedule={schedule}
          promo={promoAtual}
          onSaved={(salvos) =>
            setSchedules((state) => {
              const ids = new Set(salvos.map((s) => s.Id));
              return ordenarHorarios([
                ...state.filter((s) => !ids.has(s.Id)),
                ...salvos,
              ]);
            })
          }
        />
      )}
    </>
  );
};

export default HorariosPromocao;
